import React, { useEffect, useState, useRef } from "react";
import TabButton from "./TabButton";
import tabs from "@/data/detail-tabs.json";
import scrollToDivById from "@/utils/functions/scrollToDivById";

const TabMenu = () => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [isSticky, setIsSticky] = useState(false);
  const [parentWidth, setParentWidth] = useState("100%");
  const menuRef = useRef(null);
  const initialTopPosition = useRef(null);

  // Add these debug refs
  const debugInfo = useRef({
    lastScrollY: 0,
    reviewsPosition: null,
    mainContentBottom: null,
  });

  useEffect(() => {
    // Observe sections for active tab highlighting
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Don't change the sticky state when changing active tabs
          if (entry.isIntersecting) {
            setActiveTab(entry.target.id);
          }
        });
      },
      { threshold: 0.1, rootMargin: "-100px 0px -100px 0px" }
    );

    tabs.forEach((tab) => {
      const section = document.getElementById(tab.id);
      if (section) sectionObserver.observe(section);
    });

    // Set initial measurements
    if (menuRef.current) {
      const parentElement = menuRef.current.parentElement;
      if (parentElement) {
        const parentWidth = parentElement.getBoundingClientRect().width;
        setParentWidth(`${parentWidth}px`);
      }

      initialTopPosition.current =
        menuRef.current.getBoundingClientRect().top + window.scrollY;
    }

    const handleResize = () => {
      if (menuRef.current) {
        const parentElement = menuRef.current.parentElement;
        if (parentElement) {
          const parentWidth = parentElement.getBoundingClientRect().width;
          setParentWidth(`${parentWidth}px`);
        }
      }
    };

    const handleScroll = () => {
      if (menuRef.current && initialTopPosition.current !== null) {
        const scrollPosition = window.scrollY;
        debugInfo.current.lastScrollY = scrollPosition;

        // Get the reviews section position
        const reviewsSection = document.getElementById("reviews");
        if (reviewsSection) {
          debugInfo.current.reviewsPosition =
            reviewsSection.getBoundingClientRect().top + window.scrollY;
        }

        // Simple condition: stick if we've scrolled past the initial position
        // This is the key change - we're not trying to unstick at the bottom anymore
        const shouldBeSticky = scrollPosition > initialTopPosition.current;

        // If we were seeing the menu unsticking at the reviews section,
        // it's likely because of the bottom detection logic, so we've removed it
        setIsSticky(shouldBeSticky);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    // Initial call to set correct state
    handleScroll();

    return () => {
      sectionObserver.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={menuRef}
      className={`
        flex items-center gap-4 overflow-x-auto flex-nowrap sm:gap-8 py-3
        ${
          isSticky
            ? "fixed z-20 bg-white shadow-md transition-all duration-300 py-3 px-2"
            : "lg:py-1"
        }
      `}
      style={{
        width: isSticky ? parentWidth : "100%",
        top: isSticky ? 0 : "auto",
        left: isSticky ? (menuRef.current?.offsetLeft || 0) + "px" : "auto",
      }}
    >
      {tabs.map((tab) => (
        <TabButton
          key={tab.id}
          tab={tab}
          onClick={scrollToDivById}
          isActive={activeTab === tab.id}
        />
      ))}
    </div>
  );
};

export default TabMenu;
